import { QueryStringBuilderFactoryModule } from '@b2c/common/factories/QueryStringBuilder/QueryStringBuilder.factory';
import { ApiVersionFactoryModule } from '@b2c/common/factories/apiVersion/apiVersion.factory';
import { TODO } from '@b2c/types/index';
import { VerifiedMemberInfo } from '@b2c/types/membership';
import angular, { IHttpService, IPromise, IQService } from 'angular';
import { BugsnagService, BugsnagServiceModule } from '../bugsnag';
import { HelpersServiceModule } from '../helpers/helpers.service';
import { LocalStorageService, LocalStorageServiceModule } from '../storage';
import { ThemesServiceModule } from '../themes/themes.service';
import { VerifyEmailTokenResponse } from './types';

type AutorenewalType = 'none' | 'optional' | 'required';

export class MembershipService {
  static $inject = [
    '$q',
    '$http',
    '$filter',
    '$window',
    'QueryStringBuilderFactory',
    'ApiVersionFactory',
    'ThemesService',
    'HelpersService',
    'LocalStorageService',
    'BugsnagService',
  ];

  cookie: VerifiedMemberInfo | false = false;

  purchaseTypes = {
    new_memberships: 'MembershipPurchase',
    upgrade: 'MembershipUpgrade',
    renew: 'MembershipRenewal',
    postTermRenew: 'MembershipPostTermRenewal',
    rejoin: 'MembershipRejoin',
    renewUpgrade: 'MembershipRenewalUpgrade',
    postTermRenewUpgrade: 'MembershipPostTermRenewalUpgrade',
    rejoinUpgrade: 'MembershipRejoinUpgrade',
    renewDowngrade: 'MembershipRenewalDowngrade',
    postTermRenewDowngrade: 'MembershipPostTermRenewalDowngrade',
    rejoinDowngrade: 'MembershipRejoinDowngrade',
  };

  newPurchaseTypes = [
    'MembershipPurchase',
  ];

  purchaseTypesArray = [
    'MembershipPurchase',
    'MembershipUpgrade',
    'MembershipRenewal',
    'MembershipPostTermRenewal',
    'MembershipRejoin',
    'MembershipRenewalUpgrade',
    'MembershipPostTermRenewalUpgrade',
    'MembershipRejoinUpgrade',
    'MembershipRenewalDowngrade',
    'MembershipPostTermRenewalDowngrade',
    'MembershipRejoinDowngrade',
  ];

  renewUpgradePurchaseTypes = [
    'MembershipUpgrade',
    'MembershipRenewal',
    'MembershipPostTermRenewal',
    'MembershipRejoin',
    'MembershipRenewalUpgrade',
    'MembershipPostTermRenewalUpgrade',
    'MembershipRejoinUpgrade',
    'MembershipRenewalDowngrade',
    'MembershipPostTermRenewalDowngrade',
    'MembershipRejoinDowngrade',
  ];

  renewPurchaseTypes = [
    'MembershipRenewal',
    'MembershipPostTermRenewal',
    'MembershipRejoin',
    'MembershipRenewalUpgrade',
    'MembershipPostTermRenewalUpgrade',
    'MembershipRejoinUpgrade',
    'MembershipRenewalDowngrade',
    'MembershipPostTermRenewalDowngrade',
    'MembershipRejoinDowngrade',
  ];

  rejoinPurchaseTypes = [
    'MembershipPostTermRenewal',
    'MembershipRejoin',
    'MembershipPostTermRenewalUpgrade',
    'MembershipRejoinUpgrade',
    'MembershipPostTermRenewalDowngrade',
    'MembershipRejoinDowngrade',
  ];

  api = {
    av_membership: new this.ApiVersionFactory({
      url: '/v1/b2c/membership',
      version: 'v2',
    }),

    av_tenant_config_membership: new this.ApiVersionFactory({
      url: '/v1/b2c/tenant/product/config/membership',
      version: 'v2',
    }),

    exist: (props) => {
      var qs;

      qs = new this.QueryStringBuilderFactory({
        props: props,
        fields: {
          optional: ['email'],
        },
      });

      return this.$http({
        method: 'get',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url: this.api.av_membership.url + '/exists' + qs.query,
      });
    },

    verify: (props) => {
      var qs;

      qs = new this.QueryStringBuilderFactory({
        props: props,
        fields: {
          optional: ['eventTemplateId', 'membershipId', 'email', 'redirectUrl', 'lastName'],
        },
      });

      return this.$http<TODO>({
        method: 'get',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url: this.api.av_membership.url + '/validate' + qs.query,
      });
    },

    tenantMembershipConfig: (props) => {
      return this.$http<TODO>({
        method: 'get',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url: this.api.av_tenant_config_membership.url,
      });
    },

    verifyByEmail: (props) => {
      var qs;

      qs = new this.QueryStringBuilderFactory({
        props: props,
        fields: {
          optional: ['eventTemplateId', 'email', 'redirectUrl', 'lastName'],
        },
      });

      return this.$http<TODO>({
        method: 'get',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url: this.api.av_membership.url + '/validate/login' + qs.query,
      });
    },

    verifyEmailToken: (props) => {
      var qs = new this.QueryStringBuilderFactory({
        props: props,
        fields: {
          optional: ['membershipVerificationToken'],
        },
      });

      return this.$http<VerifyEmailTokenResponse>({
        method: 'get',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url: this.api.av_membership.url + '/verify/finish' + qs.query,
      });
    },

    search: (props) => {
      var qs = new this.QueryStringBuilderFactory({
        props: props,
        fields: {
          optional: ['eventTemplateId', 'membershipId', 'email', 'firstName', 'lastName'],
        },
      });

      return this.$http<TODO>({
        method: 'get',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url: this.api.av_membership.url + '/myMemberships' + qs.query,
      });
    },

    checkDuplicates: (props) => {
      var qs = new this.QueryStringBuilderFactory({
        props: props,
        fields: {
          required: ['membershipCategoryId', 'email', 'firstName', 'lastName'],
        },
      });

      return this.$http<TODO>({
        method: 'get',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url: this.api.av_membership.url.replace('membership', 'customers') + '/checkDuplicates' + qs.query,
      });
    },

    sendVerification: (props) => {
      var qs = new this.QueryStringBuilderFactory({
        props: props,
        fields: {
          optional: ['eventTemplateId', 'membershipId', 'email', 'firstName', 'lastName'],
        },
      });

      return this.$http<TODO>({
        method: 'get',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url: this.api.av_membership.url + '/sendVerification' + qs.query,
      });
    },

    list: (props) => {
      return this.$http<TODO>({
        method: 'get',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url:
          this.api.av_membership.url +
          '/categoriesBySalesChannel?salesChannels=online&action=NEW_MEMBERSHIPS&membershipLevelGroup=individualAndFamily',
        // url: '/assets/assets/data/membership.json'
      });
    },

    checkout: (props) => {
      var ep = '/v2/b2c/orders/membership',
        data = {
          membershipId: props.membershipId,
          membershipCategoryId: props.membershipCategoryId,
          purchaserInfo: props.purchaserInfo,
          gifterInfo: props.gifterInfo,
          membershipCards: props.membershipCards,
          notifyMember: props.notifyMember,
          notifyPurchaser: props.notifyPurchaser,
          sendRenewalEmailToPurchaser: props.sendRenewalEmailToPurchaser,
          sendRenewalEmailToMember: props.sendRenewalEmailToMember,
          isGift: props.isGift,
          giftMessage: props.giftMessage,
        };

      if (props.purchaserInfo.address) {
        data.purchaserInfo.billingAddress1 = props.purchaserInfo.address.streetAddress1;
        data.purchaserInfo.billingAddress2 = props.purchaserInfo.address.streetAddress2;
        data.purchaserInfo.billingCity = props.purchaserInfo.address.city;
        data.purchaserInfo.billingState = props.purchaserInfo.address.state;
        data.purchaserInfo.billingCountry = props.purchaserInfo.address.country;
        data.purchaserInfo.billingZipCode = props.purchaserInfo.zipCode;
      }

      return this.$http<TODO>({
        method: 'post',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url: ep + '/purchase',
        data: data,
      });
    },

    category: (props) => {
      var q = '',
        qs;

      props = props || {};

      if (!props.salesChannels) {
        props.salesChannels = 'online';
      } else if (props.salesChannels === 'any') {
        delete props.salesChannels;
      }

      qs = new this.QueryStringBuilderFactory({
        props: props,
        fields: {
          optional: ['action', 'salesChannels'],
        },
      });

      q = qs.query;

      return this.$http<TODO>({
        method: 'get',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url: this.api.av_membership.url + '/categories' + '/' + props.id + q,
      });
    },

    getEligibilityForCategory: (props) => {
      var q = '',
        qs;

      qs = new this.QueryStringBuilderFactory({
        props: props,
        fields: {
          requires: ['membershipId'],
          optional: ['saleChannels', 'actions'],
        },
      });

      q = qs.query;
      return this.$http<TODO>({
        method: 'get',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url: this.api.av_membership.url + '/eligibleoptions/levels/' + q,
      });
    },

    getEligibilityForCategoryBulk: (props) => {
      var q = '',
        qs;

      qs = new this.QueryStringBuilderFactory({
        props: props,
        fields: {
          requires: ['membershipIds'],
          optional: ['saleChannels', 'actions'],
        },
      });

      q = qs.query;
      return this.$http<TODO>({
        method: 'get',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url: this.api.av_membership.url + '/eligibleoptions/levels/multiple' + q,
      });
    },

    detail: (props) => {
      return this.$http<TODO>({
        method: 'get',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url: this.api.av_membership.url + '/' + props.membershipId,
      });
    },

    upcoming: (props) => {
      var qs;

      qs = new this.QueryStringBuilderFactory({
        props: props,
        fields: {
          optional: ['active'],
        },
      });

      return this.$http<TODO>({
        method: 'get',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url: this.api.av_membership.url + '/recent/' + props.membershipId + qs.query,
      });
    },

    upcomingByEmail: (props) => {
      var qs;

      qs = new this.QueryStringBuilderFactory({
        props: props,
        fields: {
          optional: ['active'],
          requires: ['email'],
        },
      });

      return this.$http<TODO>({
        method: 'get',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url: this.api.av_membership.url + '/recent' + qs.query,
      });
    },

    calculate: (props) => {
      var qs;

      if (props && !props.action) {
        props.action = 'MembershipUpgrade';
      }

      qs = new this.QueryStringBuilderFactory({
        props: props,
        fields: {
          optional: ['membershipId', 'action', 'offeringId', 'levelId', 'pricePointId'],
        },
      });

      return this.$http<TODO>({
        method: 'get',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url: this.api.av_membership.url + '/validate/checkout' + qs.query,
      });
    },

    recurring: (props) => {
      return this.$http<TODO>({
        method: 'get',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url: this.api.av_membership.url + '/' + props.membershipId + '/recurring/details',
      });
    },
  };

  constructor(
    private $q: IQService,
    private $http: IHttpService,
    private $filter: TODO,
    private $window: TODO,
    private QueryStringBuilderFactory: TODO,
    private ApiVersionFactory: TODO,
    private ThemesService: TODO,
    private HelpersService: TODO,
    private localStorageService: LocalStorageService,
    private bugsnagService: BugsnagService
  ) {}

  /*
   * set the primary, auxiliary and other (multiple non-auxiliary memberships)
   */
  setCookie(memberships: TODO[], tenantId: string, verifiedMember: TODO, confirmedByEmail?: boolean) {
    memberships = Array.isArray(memberships) ? memberships : [];

    let primary: TODO;
    const other: TODO[] = [];
    const auxiliary: TODO[] = [];
    const verifiedMemberInfo = verifiedMember
      ? {
          membershipId: verifiedMember.membershipId,
          id: verifiedMember.id,
          categoryId: verifiedMember.categoryId,
          name: this.getMemberName(verifiedMember),
          membershipCategory: verifiedMember.membershipCategory,
          externalMembershipId: verifiedMember.externalMembershipId,
          acmeBarcode: verifiedMember.acmeBarcode,
          email: verifiedMember.email,
          auxiliary: verifiedMember.auxiliary,
        }
      : undefined;

    $.cookie.json = true;

    this.logNoneActiveOrLapsedMemberships(memberships);

    primary = memberships
      .filter((membership) => !membership.auxiliaryMembership && membership.membershipStanding === 'active')
      .sort(this.HelpersService.sortByPropName('membershipExpirationDate'))
      .map(this.getMembershipProperties)
      .pop();

    for (let i = 0, ii = memberships.length; i < ii; i++) {
      const isLastChanceToSetPrimary = i === memberships.length - 1;

      if (memberships[i].auxiliaryMembership) {
        auxiliary.push(this.getMembershipProperties(memberships[i]));
      } else if (
        !primary &&
        (memberships[i].membershipStanding === 'active' ||
          memberships[i].membershipStanding === 'lapsed' ||
          isLastChanceToSetPrimary)
      ) {
        // the first non-aux is primary if it's active OR
        // if we've gone through everything and there is no aux and no active membership, then the primary can be an upcoming membership
        primary = this.getMembershipProperties(memberships[i]);
      } else if ((primary && primary.membershipId !== memberships[i].id) || !primary) {
        //these are other non-aux memberships (upcoming or other active bases)
        other.push(this.getMembershipProperties(memberships[i]));
      }
    }

    this.localStorageService.setItem<VerifiedMemberInfo>('membership', {
      member: primary,
      other: other,
      auxiliary: auxiliary,
      tenantId: tenantId,
      verifiedMember: verifiedMemberInfo,
      confirmedByEmail: confirmedByEmail || this.isConfirmedByEmail(),
    });
  }

  private logNoneActiveOrLapsedMemberships(memberships: TODO[]) {
    if (memberships.length === 0) {
      return;
    }

    const actives = memberships.filter(
      (membership) => !membership.auxiliaryMembership && membership.membershipStanding === 'active'
    );

    if (actives.length > 0) {
      return;
    }

    const lapsed = memberships.filter(
      (membership) => !membership.auxiliaryMembership && membership.membershipStanding === 'lapsed'
    );

    if (lapsed.length > 0) {
      return;
    }

    const error = new Error(`No primary memberships with membershipStanding "active" or "lapsed" were found`);
    this.bugsnagService.notify(error, function (event) {
      event.severity = 'info';
      event.context = 'verified-membership';
    });
  }

  getCookie() {
    $.cookie.json = true;
    this.cookie = this.localStorageService.getItem<VerifiedMemberInfo>('membership') ?? false;
  }

  getMembershipProperties(membership) {
    return {
      membershipId: membership.id,
      name: membership && membership.primaryCardholder ? membership.primaryCardholder.name : '',
      firstName: membership && membership.primaryCardholder ? membership.primaryCardholder.firstName : '',
      lastName: membership && membership.primaryCardholder ? membership.primaryCardholder.lastName : '',
      categoryId: membership.categoryId,
      externalMembershipId: membership.externalMembershipId,
      membershipStanding: membership.membershipStanding,
      membershipExpirationDate: membership.membershipExpirationDate,
      acmeMembershipNumber: membership.acmeMembershipNumber,
    };
  }

  /*
   * Get membership ids for the verified member with option to exclude expired memberships
   * Also, if a list of current memberships is provided, only return the valid ones from that list.
   * We do not return more than one base membership here.
   */
  getMembershipIds(excludeExpiredMemberships?: boolean, currentMembershipIds?: TODO[], withoutExtension?: boolean) {
    var memberships: TODO[] = [];
    var membershipIds;

    this.getCookie();

    if (this.cookie && this.cookie.member) {
      memberships.push(this.cookie.member);
    }

    if (this.cookie && Array.isArray(this.cookie.auxiliary) && this.cookie.auxiliary.length) {
      memberships = memberships.concat(this.cookie.auxiliary);
    }

    //filter out expired memberships
    if (!withoutExtension) {
      memberships = memberships.filter(
        excludeExpiredMemberships
          ? (membership) => {
              return !this.isExpiredMembership(membership);
            }
          : (membership) => {
              return membership.membershipId;
            }
      );
    } else {
      memberships = memberships.filter(
        excludeExpiredMemberships
          ? (membership) => {
              return !this.isExpiredMembershipWithoutExtension(membership);
            }
          : (membership) => {
              return membership.membershipId;
            }
      );
    }

    //filter out memberships not in the list provided
    if (Array.isArray(currentMembershipIds)) {
      memberships = memberships.filter((membership) => {
        var r = false,
          i,
          ii;
        //The type of currentMembershipIds item is string and membership.membershipId is an int. So using == here.
        for (i = 0, ii = currentMembershipIds.length; i < ii; i++) {
          if (currentMembershipIds[i] == membership.membershipId) {
            r = true;
            break;
          }
        }
        return r;
      });
    }

    membershipIds = memberships.map((membership) => {
      return membership.membershipId;
    });

    return membershipIds;
  }

  /**
   * Return the base membership, if found. Otherwise, returns the first auxiliary membership
   */
  getPrimaryMember(baseOnly, excludeExpired = false) {
    let member: VerifiedMemberInfo['member'] | false = false;

    this.getCookie();

    if (this.cookie && this.cookie.member && this.cookie.member.membershipId) {
      member = this.cookie.member;
      if (excludeExpired && this.isExpiredMembership(member)) {
        member = false;
      }
    } else if (!baseOnly && this.cookie && Array.isArray(this.cookie.auxiliary) && this.cookie.auxiliary.length) {
      this.cookie.auxiliary.forEach((auxMembership) => {
        if (auxMembership && auxMembership.membershipId) {
          member = auxMembership;
        }
      });
    }

    return member;
  }

  /**
   * Checks if member verified with email
   */
  isConfirmedByEmail(): boolean {
    this.getCookie();
    return this.cookie && !!this.cookie.verifiedMember && this.cookie.confirmedByEmail;
  }

  findMembership(id, list) {
    let result: TODO | false = false;

    for (let i = 0, ii = list.length; i < ii; i++) {
      if (list[i].membershipId && list[i].membershipId === id) {
        result = {
          index: i,
          membership: list[i],
        };
        break;
      }
    }
    return result;
  }

  /**
   * Gets all membership categoryIds user has assigned
   *      
   * @returns {String[]}
   */
  getVerifiedMemberCategoryIds() {
    let memberCategoryId: string = '';
    let categoryIds: string[] = [];
    let auxiliaryIds: string[] = [];

    this.getCookie();

    memberCategoryId = this.HelpersService.path(this.cookie, ['member', 'categoryId']);

    if (memberCategoryId) {
      categoryIds.push(memberCategoryId);
    }

    auxiliaryIds = this.HelpersService.path(this.cookie, ['auxiliary'], []).map((item: TODO) => {
      return item.categoryId;
    });

    return categoryIds.concat(auxiliaryIds);
  }

  /**
   * Returns the verified membership if available
   */
  getVerifiedMember(): TODO | false {
    let member: VerifiedMemberInfo['verifiedMember'] | false = false;

    this.getCookie();

    if (this.cookie && this.cookie.verifiedMember) {
      member = this.cookie.verifiedMember;
    }

    return member;
  }

  /**
   * Given a membership, we can get the current logged carholder info
   *
   * @returns cardholder
   */
  getVerifiedMemberCardholder(membership: TODO): TODO | undefined {
    var verifiedMember = this.getVerifiedMember();
    return verifiedMember && membership
      ? membership.cardholders.find((cardholder) => {
          return cardholder.id === verifiedMember.id;
        })
      : undefined;
  }

  /**
   * Checks if membership standing is active and takes the company's
   * membership benefits extension (grace period) into account.
   *
   * ENUMS for `membershipStanding`
   *
   * - // future
   *   - NEW (0, "new", "New"),
   * - // current
   *   - ACTIVE (1, "active", "Active"),
   * - // canceled
   *   - DROPPED (2, "dropped", "Dropped"),
   * - // expired
   *   - LAPSED (3, "lapsed", "Lapsed"),
   *   - NO_MEMBERSHIP (4, "noMembership", "No Membership"),
   *   - REPLACED (5, "replaced", "Replaced");
   */
  checkMembershipActive(data: VerifiedMemberInfo | false) {
    var membership = this.HelpersService.path(data, ['member']);
    var membershipStanding = this.HelpersService.path(data, ['member', 'membershipStanding'], '').toLowerCase();

    return membershipStanding === 'active' || membershipStanding === 'new' || !this.isExpiredMembership(membership);
  }

  isMemberActive() {
    this.getCookie();

    return this.checkMembershipActive(this.cookie);
  }

  /**
   * @param eventModel.memberOnlyEvent Event model coming from the event details API have the `memberOnlyEvent` property
   */
  isMemberOnlyEvent(eventModel: { memberOnlyEvent }): boolean {
    if (eventModel.memberOnlyEvent) {
      return true;
    }

    var membershipRestrictions = this.HelpersService.path(eventModel, ['membership', 'restrictions'], []);

    return membershipRestrictions.length > 0;
  }

  validateMemberCanPurchaseEvent(
    eventModel: TODO,
    membershipCategoryIdsInCart: TODO[]
  ): {
    isMemberActive: boolean;
    isMemberOnlyEvent: boolean;
    hasEligibleMembership: boolean;
    hasEligibleMembershipInCart: boolean;
    result: boolean;
  } {
    var isMemberActive = this.isMemberActive();
    var isMemberOnlyEvent = this.isMemberOnlyEvent(eventModel);
    var membershipCategoryIdsInCartArray = membershipCategoryIdsInCart || [];

    var verifiedMember = this.getVerifiedMember();
    var categoryIds = (verifiedMember) ? this.getVerifiedMemberCategoryIds() : [];
    var membershipRestrictions = this.HelpersService.path(eventModel, ['membership', 'restrictions'], []);
    var hasEligibleMembership = membershipRestrictions.some(function (restriction) {
      return  categoryIds.indexOf(restriction.id) >= 0;
    });

    var hasEligibleMembershipInCart = membershipRestrictions.some((restriction) => {
      return membershipCategoryIdsInCartArray.indexOf(restriction.id) > -1;
    });

    var result = true;
    if (isMemberOnlyEvent) {
      result = (isMemberActive && hasEligibleMembership) || hasEligibleMembershipInCart;
    }

    return {
      isMemberActive: isMemberActive,
      isMemberOnlyEvent: isMemberOnlyEvent,
      hasEligibleMembership: hasEligibleMembership,
      hasEligibleMembershipInCart: hasEligibleMembershipInCart,
      result: result,
    };
  }

  getMemberOnlyCalloutMessage(eventModel: TODO): string {
    var flags = this.validateMemberCanPurchaseEvent(eventModel, []);

    if (!flags.isMemberActive) {
      return this.ThemesService.getTextVariable('event-member-only-callout-membership-required', 'Membership required');
    }

    if (!flags.hasEligibleMembership) {
      return this.ThemesService.getTextVariable(
        'event-member-only-callout-not-eligible-for-purchase',
        'Your membership is not eligible for this event'
      );
    }

    return this.ThemesService.getTextVariable(
      'event-member-only-callout-eligible-for-purchase',
      'Eligible for purchase'
    );
  }

  /**
   * @note We cannot directly call the AnalyticsService because it would cause a circular dependency.
   */
  getMemberVerifyEventMetadata(params: { tenantId: number }) {
    var member = this.getVerifiedMember();

    var currentQueryParams = this.HelpersService.parseQueryParams(window.location.href);
    delete currentQueryParams.ref; // Delete current ref here to avoid duplicate ref values
    var ref =
      window.location.toString().replace(location.search, '') + this.HelpersService.getQueryString(currentQueryParams);

    if (member) {
      return {
        event: {
          name: 'Membership::Profile',
          category: 'Header',
        },
        href: this.HelpersService.makeUrl(['/membership', params.tenantId, 'profile'], {
          ref: ref,
          membershipId: member.membershipId,
        }),
      };
    }

    if (window.location.pathname.match(/^\/auth/)) {
      return {
        event: {
          name: 'Membership::Login',
          category: 'Header',
        },
        href: this.HelpersService.makeUrl(['/auth', params.tenantId, 'members'], { ref: ref }),
      };
    }

    return {
      event: {
        name: 'Membership::Login',
        category: 'Header',
        label: 'Ref: ' + window.location.href,
      },
      href: this.HelpersService.makeUrl(['/auth', params.tenantId, 'members'], { ref: ref }),
    };
  }

  getMemberName(member: { name?: string; firstName?: string; lastName?: string }) {
    if (member.name) {
      return member.name;
    }

    return [member.firstName, member.lastName].filter((value) => !!value).join(' ');
  }

  killCookie() {
    return this.localStorageService.removeItem('membership');
  }

  /*
   * Checks if the verified member has a membership for the provided membership level
   * Returns the membership if found
   */
  checkVerifiedMembershipCategory(membershipCategory, tenantId) {
    let r: VerifiedMemberInfo['member'] | false = false;

    if (membershipCategory && tenantId) {
      this.getCookie();

      if (this.checkVerifiedMembership(tenantId) && this.cookie) {
        if (this.cookie.member) {
          if (membershipCategory.id === this.cookie.member.categoryId) {
            r = this.cookie.member;
          }
        }
        if (!r && Array.isArray(this.cookie.auxiliary)) {
          for (let i = 0, ii = this.cookie.auxiliary.length; i < ii; i++) {
            if (membershipCategory.id === this.cookie.auxiliary[i].categoryId) {
              r = this.cookie.auxiliary[i];
              break;
            }
          }
        }
        if (!r && Array.isArray(this.cookie.other)) {
          for (let i = 0, ii = this.cookie.other.length; i < ii; i++) {
            if (membershipCategory.id === this.cookie.other[i].categoryId) {
              r = this.cookie.other[i];
              break;
            }
          }
        }
      }
    }

    return r;
  }

  /*
   * Checks if the verified member has a membership for the provided membership id
   * Returns the membership if found
   */
  checkVerifiedMembershipId(membershipId, tenantId) {
    let r: VerifiedMemberInfo['member'] | false = false;

    membershipId = membershipId ? parseInt(membershipId, 10) : null;

    if (!isNaN(membershipId) && tenantId) {
      this.getCookie();

      if (this.checkVerifiedMembership(tenantId) && this.cookie) {
        if (this.cookie.member) {
          if (membershipId === this.cookie.member.membershipId) {
            r = this.cookie.member;
          }
        }
        if (!r && Array.isArray(this.cookie.auxiliary)) {
          for (let i = 0, ii = this.cookie.auxiliary.length; i < ii; i++) {
            if (membershipId === this.cookie.auxiliary[i].membershipId) {
              r = angular.extend({}, this.cookie.auxiliary[i], { auxiliaryMembership: true });
              break;
            }
          }
        }
        if (!r && Array.isArray(this.cookie.other)) {
          for (let i = 0, ii = this.cookie.other.length; i < ii; i++) {
            if (membershipId === this.cookie.other[i].membershipId) {
              r = this.cookie.other[i];
              break;
            }
          }
        }
      }
    }

    return r;
  }

  /*
   * Checks if the verified member has an upcoming renewal membership
   * Returns the membership if found
   */
  findUpcomingMembership(membership) {
    let r: VerifiedMemberInfo['member'] | false = false;

    if (membership.membershipStanding && membership.membershipStanding === 'active') {
      if (this.cookie && this.cookie.member) {
        if (
          this.cookie.member.membershipStanding &&
          this.cookie.member.membershipStanding === 'new' &&
          membership.acmeMembershipNumber &&
          membership.acmeMembershipNumber === this.cookie.member.acmeMembershipNumber
        ) {
          r = this.cookie.member;
        }
      }
      if (!r && this.cookie && Array.isArray(this.cookie.auxiliary)) {
        for (let i = 0, ii = this.cookie.auxiliary.length; i < ii; i++) {
          if (
            this.cookie.auxiliary[i].membershipStanding &&
            this.cookie.auxiliary[i].membershipStanding === 'new' &&
            membership.acmeMembershipNumber &&
            membership.acmeMembershipNumber === this.cookie.auxiliary[i].acmeMembershipNumber
          ) {
            r = angular.extend({}, this.cookie.auxiliary[i], { auxiliaryMembership: true });
            break;
          }
        }
      }
      if (!r && this.cookie && Array.isArray(this.cookie.other)) {
        for (let i = 0, ii = this.cookie.other.length; i < ii; i++) {
          if (
            this.cookie.other[i].membershipStanding &&
            this.cookie.other[i].membershipStanding === 'new' &&
            membership.acmeMembershipNumber &&
            membership.acmeMembershipNumber === this.cookie.other[i].acmeMembershipNumber
          ) {
            r = this.cookie.other[i];
            break;
          }
        }
      }
    }

    return r;
  }

  isExpiredMembership(membership) {
    if (!this.ThemesService.isCompanyLoaded()) {
      return false;
    }

    var expiresInDays;
    var membershipBenefitsExtension = this.ThemesService.getCompany('membershipBenefitsExtension') || 0;
    var membershipExpirationDate = this.HelpersService.path(membership, ['membershipExpirationDate']);
    var membershipStanding = this.HelpersService.path(membership, ['membershipStanding'], '').toLowerCase();

    if (!isNaN(this.ThemesService.getCompany('membershipBenefitsExtension') && membershipBenefitsExtension > 0)) {
      const expirationDate = new Date(membershipExpirationDate);
      expiresInDays =
        expirationDate.setDate(expirationDate.getDate() + membershipBenefitsExtension) - new Date().getTime();

      if (!isNaN(expiresInDays) && expiresInDays > 0) {
        return false;
      }
    }

    expiresInDays = new Date(membershipExpirationDate).getTime() - new Date().getTime();

    // using !== active here as it can be dropped or lapsed and we want only current memberships
    return membershipStanding !== 'active' || isNaN(expiresInDays) || expiresInDays <= 0;
  }

  isExpiredMembershipWithoutExtension(membership) {
    var expiresInDays;
    var membershipExpirationDate = this.HelpersService.path(membership, ['membershipExpirationDate']);
    var membershipStanding = this.HelpersService.path(membership, ['membershipStanding'], '').toLowerCase();

    expiresInDays = new Date(membershipExpirationDate).getTime() - new Date().getTime();

    // using !== active here as it can be dropped or lapsed and we want only current memberships
    return membershipStanding !== 'active' || isNaN(expiresInDays) || expiresInDays <= 0;
  }

  /*
   * Membership cookie matches tenantId and has either a non-aux membership or at least one auxiliary membership that's not expired
   */
  checkVerifiedMembership(tenantId, excludeExpiredMemberships = false) {
    var r = {
      verified: false,
      membershipIds: [],
    };

    if (tenantId) {
      this.getCookie();

      if (this.cookie && this.cookie.tenantId === tenantId) {
        r.membershipIds = this.getMembershipIds(excludeExpiredMemberships);
        if (r.membershipIds.length) {
          r.verified = true;
        }
      }
    }

    return r.verified ? r : false;
  }

  checkExpiredVerifiedMembership(tenantId) {
    return !this.checkVerifiedMembership(tenantId, true);
  }

  init() {
    return true;
  }

  getPricePointCount(membership) {
    var offering,
      priceList,
      r = 0,
      i,
      ii,
      j,
      jj;

    if (membership.hasOwnProperty('offerings') && Array.isArray(membership.offerings) && membership.offerings.length) {
      for (i = 0, ii = membership.offerings.length; i < ii; i++) {
        offering = membership.offerings[i];
        if (offering.hasOwnProperty('priceLists') && Array.isArray(offering.priceLists) && offering.priceLists.length) {
          for (j = 0, jj = offering.priceLists.length; j < jj; j++) {
            priceList = offering.priceLists[j];
            if (priceList.hasOwnProperty('prices') && Array.isArray(priceList.prices) && priceList.prices.length) {
              r = r + priceList.prices.length;
            }
          }
        }
      }
    }

    return r;
  }

  findFirstNonAutoRenewOfferingIndex(offerings: TODO[]) {
    var i;

    for (i = 0; i < offerings.length; i++) {
      if (!this.isAutorenewalType(offerings[i].autorenewalType)) {
        return i;
      }
    }

    return 0;
  }

  isAutorenewalType(autorenewalType: AutorenewalType) {
    if (autorenewalType == null || autorenewalType === 'none') {
      return false;
    }

    return ['optional', 'required'].includes(autorenewalType);
  }

  isAutorenew(value: { autorenewalType: AutorenewalType; isAutorenew: boolean }) {
    switch (value.autorenewalType) {
      case 'required':
        return true;

      case 'optional':
        return value.isAutorenew;

      default:
        return false;
    }
  }

  findPricePointById(membership, pricePointId, offeringId) {
    var i, ii;
    var j, jj;
    var k, kk;
    var offering;
    var priceList;
    var result: TODO | false = false;

    if (membership.hasOwnProperty('offerings') && Array.isArray(membership.offerings) && membership.offerings.length) {
      for (i = 0, ii = membership.offerings.length; i < ii; i++) {
        offering = membership.offerings[i];
        if (
          offering.id === offeringId &&
          offering.hasOwnProperty('priceLists') &&
          Array.isArray(offering.priceLists) &&
          offering.priceLists.length
        ) {
          for (j = 0, jj = offering.priceLists.length; j < jj; j++) {
            priceList = offering.priceLists[j];
            if (priceList.hasOwnProperty('prices') && Array.isArray(priceList.prices) && priceList.prices.length) {
              for (k = 0, kk = priceList.prices.length; k < kk; k++) {
                if (priceList.prices[k].personType.id === pricePointId) {
                  result = {
                    offeringIndex: i,
                    offering: offering,
                    pricePointIndex: k,
                    pricePoint: priceList.prices[k],
                  };
                  break;
                }
              }
            }
          }
        }
      }
    }

    return result;
  }

  isMembershipType(itemType) {
    var type,
      r = false;
    for (type in this.purchaseTypes) {
      if (this.purchaseTypes.hasOwnProperty(type) && this.purchaseTypes[type]) {
        if (this.purchaseTypes[type] === itemType) {
          r = type;
        }
      }
    }
    return r;
  }

  isPostTermMembershipType(itemType) {
    var type,
      r = false;

    for (type in this.rejoinPurchaseTypes) {
      if (this.rejoinPurchaseTypes.hasOwnProperty(type) && this.rejoinPurchaseTypes[type]) {
        if (this.rejoinPurchaseTypes[type] === itemType) {
          r = true;
        }
      }
    }

    return r;
  }

  orderLevels(memberships, primaryCategoryId) {
    var i,
      ii,
      sorted = memberships;

    if (Array.isArray(memberships)) {
      sorted = memberships.sort((a, b) => {
        var r = 0;
        a.displayOrder = a.displayOrder || 0;
        b.displayOrder = b.displayOrder || 0;

        if (a.displayOrder > b.displayOrder) {
          r = 1;
        }
        if (a.displayOrder < b.displayOrder) {
          r = -1;
        }
        return r;
      });
    }

    if (primaryCategoryId) {
      for (i = 0, ii = sorted.length; i < ii; i++) {
        if (sorted[i].id === primaryCategoryId) {
          sorted[i].isCurrent = true;
          sorted.unshift(sorted.splice(i, 1)[0]);
          break;
        }
      }
    }
    return sorted;
  }

  isCSR(tenantId) {
    var r = false,
      CSRCookie;

    CSRCookie = window.CONFIG.isCSR;

    if (CSRCookie && (CSRCookie === 'true' || CSRCookie === true)) {
      r = true;
    }

    return r;
  }

  checkSendConfirmationForChannel(confirmationEmailRestrictions, saleChannel) {
    let result = false;

    confirmationEmailRestrictions = Array.isArray(confirmationEmailRestrictions) ? confirmationEmailRestrictions : [];

    if (saleChannel) {
      for (let i = 0, ii = confirmationEmailRestrictions.length; i < ii; i++) {
        if (
          confirmationEmailRestrictions[i] &&
          confirmationEmailRestrictions[i].hasOwnProperty('saleChannel') &&
          confirmationEmailRestrictions[i].saleChannel === saleChannel &&
          confirmationEmailRestrictions[i].canSendConfirmationEmail
        ) {
          result = true;
        }
      }
    }
    return result;
  }

  canSendMemberCardsForAction(membershipOffering, action, tenantId) {
    var r = false,
      i,
      ii,
      channel = this.isCSR(tenantId) ? 'customerRep' : 'online';

    if (membershipOffering && action && Array.isArray(membershipOffering.actions)) {
      for (i = 0, ii = membershipOffering.actions.length; i < ii; i++) {
        if (
          membershipOffering.actions[i].action &&
          membershipOffering.actions[i].action.toUpperCase() === action.toUpperCase()
        ) {
          r = this.checkSendConfirmationForChannel(
            membershipOffering.actions[i].confirmationEmailRestrictions,
            channel
          );
        }
      }
    }
    return r;
  }

  getAuxiliaryGroups(membershipLevels) {
    var i, ii;
    var j, jj;
    var groups: TODO[] = [];
    var objGroups: TODO = {};

    for (i = 0, ii = membershipLevels.length; i < ii; i++) {
      if (membershipLevels[i].auxiliary && membershipLevels[i].hasOwnProperty('membershipLevelCollection')) {
        for (j = 0, jj = membershipLevels[i].membershipLevelCollection.length; j < jj; j++) {
          objGroups[membershipLevels[i].membershipLevelCollection[j].id] =
            membershipLevels[i].membershipLevelCollection[j];
        }
      }
    }

    for (var key in objGroups) {
      groups.push(objGroups[key]);
    }

    groups = this.$filter('orderBy')(groups, 'displayOrder');
    return groups;
  }

  getByAuxiliaryGroup(membershipLevels, groupId) {
    var result: TODO[] = [];

    for (let i = 0, ii = membershipLevels.length; i < ii; i++) {
      if (membershipLevels[i].auxiliary && membershipLevels[i].hasOwnProperty('membershipLevelCollection')) {
        for (let j = 0, jj = membershipLevels[i].membershipLevelCollection.length; j < jj; j++) {
          if (membershipLevels[i].membershipLevelCollection[j].id === groupId) {
            result.push(membershipLevels[i]);
          }
        }
      }
    }
    return result;
  }

  // Only allow new membership purchase if the user is not verified, OR if the user is verified and it is an auxiliary membership
  // Or if verified member has only aux and it is a base
  canJoin(tenantId, membershipCategory) {
    var isVerifiedMember = this.checkVerifiedMembership(tenantId),
      hasBaseMembership = this.getPrimaryMember(true),
      r = false;
    if (isVerifiedMember) {
      if ((membershipCategory.auxiliary && !this.hasAuxInBaseMembership(membershipCategory)) || !hasBaseMembership) {
        r = true;
      }
    } else {
      r = true;
    }

    return r;
  }

  // Only allow GIFT membership purchase if the membership offering allows it.
  canGift(tenantId, membershipCategory) {
    var r = false;
    if (membershipCategory.rules && membershipCategory.rules.canGift) {
      r = true;
    }

    return r;
  }

  hasAuxInBaseMembership(membership) {
    return (
      this.cookie &&
      this.cookie.auxiliary.some((val) => {
        return membership.id === val.categoryId;
      })
    );
  }

  isNewOrRejoinType(membershipType) {
    return (
      (membershipType && this.rejoinPurchaseTypes.indexOf(membershipType) > -1) ||
      membershipType === this.purchaseTypes.new_memberships
    );
  }

  processMemberships(memberships: TODO[]) {
    for (let i = 0, ii = memberships.length; i < ii; i++) {
      const membership = memberships[i];
      if (Array.isArray(membership.cardholders)) {
        for (let j = 0, jj = membership.cardholders.length; j < jj; j++) {
          if (
            membership.cardholders[j].hasOwnProperty('cardType') &&
            membership.cardholders[j].cardType === 'primary'
          ) {
            membership.primaryCardholder = membership.cardholders[j];
            membership.primaryCardholder.name =
              membership.primaryCardholder.name ||
              (membership.primaryCardholder.firstName + ' ' || '') + (membership.primaryCardholder.lastName || '');
          }
        }
      }
    }

    return memberships;
  }

  isAuxiliaryMember(membership) {
    var r = false;
    if (membership.auxiliary) {
      r = membership.auxiliary;
    }
    return r;
  }

  verifyMemberSession(tenantId: string): IPromise<void> {
    const q = this.$q.defer<void>();

    if (!this.checkVerifiedMembership(tenantId)) {
      q.resolve();

      return q.promise;
    }

    const member = this.getVerifiedMember();

    if (!member) {
      q.resolve();

      return q.promise;
    }

    const props = {
      tenantId,
      active: '1',
      email: member.email || false,
    };

    const promise = this.isConfirmedByEmail()
      ? this.api.upcomingByEmail(props)
      : this.api.upcoming({ ...props, membershipId: member.externalMembershipId });

    promise
      .then((response) => {
        const memberships = this.processMemberships(response.data.payload);
        this.setCookie(memberships, tenantId, this.getVerifiedMember());

        q.resolve();
      })
      .catch(() => {
        q.reject();
      });

    return q.promise;
  }
}

export const MembershipServiceModule = angular
  .module('acme.common.service.membership', [
    QueryStringBuilderFactoryModule,
    ApiVersionFactoryModule,
    ThemesServiceModule,
    HelpersServiceModule,
    LocalStorageServiceModule,
    BugsnagServiceModule,
  ])
  .service('MembershipService', MembershipService).name;
